import type { TrayChamberView } from '@ctcells/api'
import type {
  BatchTracking,
  ChamberTrack,
  DispenseRecord,
  DispenseResult,
  TrayTrack,
  TrayTrackWhere,
} from '@ctcells/contract'

import type { ChamberPhase, ChamberStage } from '../../figures/chamber3d/chamberScene.ts'

/**
 * **챔버(웰)마다의 진행** — 어휘와 판정의 단일 출처(eric 2026-09-28 「각각의 챔버별.. 시료별로.. 진행상태를
 * 모두 일치시키지 말고. 스텝 별로.. 다르게 진행사항을 표시해줘」).
 *
 * 🔴 **화면은 상태를 추론하지 않는다.** 챔버마다 갈리는 것은 **서버가 그 웰을 가리켜 말한 것**뿐이다.
 *
 * 🔵 **서버가 이제 웰마다 말한다**(core 948d712d · API 계약 0.28.15 · 2026-10-22 로컬 실측 — `GET /batches/{id}/tracking` 과 구독
 *    `batch_tracking` 의 `ChamberTrack`). 종전 이 자리의 「원심 · 어셈블러는 서버가 웰을 말하지 않는다(BR-98)」 표는 **풀렸다**:
 *
 * | 칸 | 뜻 | 화면 |
 * | --- | --- | --- |
 * | `active{module_id, stage}` | 그 공정이 «지금» 그 웰을 한다(배치 running 일 때만 · 없으면 null) | 「진행」 배지 · 작업 중 강조 |
 * | `phase_mark` | 원심 대기/중/완료 · 어셈 대기/UV 중/UV 완료/폐액 배출 중/어셈 완료(전개 규칙 `process.phase_marks`) | 한 마디 글자 |
 * | `recipe_step{no, total, liquid_id, label}` | 염색 «k/N 시약» | 한 마디 글자 |
 * | `condition` | 정상 · 실패 · 폐기 · 확인 필요 · 판정 대기 | 편차 낱말 |
 *
 * ⇒ 서버 추적이 그 웰을 실어 오면 **`trackingProgress.ts` 가 그대로 읽는다**(`basis: 'tracking'` · `useSampleFlow`). 아래 갈래
 *   (분주 기록 `well` · 트레이 뷰 `tray` · 모듈 상태 `module` · 이송 `transfer` · 실행 끝 `settled`)는 **추적을 못 받았을 때만** 선다
 *   (조회 중 · 실패 · 그 웰이 응답에 없음). 서버 값이 있는데 이쪽으로 짐작하지 않는다.
 * ⚠ 실측: 시작 전 · 원심 대기 웰은 `phase_mark: centrifuge_waiting` · `active: null` 이고, 폐기된 웰에도 마디가 남는다 —
 *   그래서 `condition` 이 마디보다 먼저다(`trackingChamberProgress`).
 *
 * 🔴 **이 어휘 위에 단계별 3D 표현이 선다**(다음 작업 · `figures/chamber3d/*`). 모양은 여기서 정하지 않는다.
 */

/**
 * 챔버 한 개의 상태. 🔴 **화면 글자는 준비 중 · 진행 · 완료 · 실패 — eric 2026-09-28**(「아직 말고 준비 중」).
 * 코드 어휘(`pending` 등)는 그대로 두고 글자는 기존 챔버 키를 쓴다(`bundleCardModel.chamberNote`).
 */
export type ChamberProgressStatus =
  'pending' | 'active' | 'done' | 'failed' | 'transfer' | 'settled'

export interface ChamberProgress {
  /** 어느 공정 단계의 말인가 — 챔버 도형의 단계 어휘 그대로(`ChamberStage`) */
  stage: ChamberStage
  status: ChamberProgressStatus
  /**
   * 🔴 **근거.** `well` = 서버가 그 웰을 가리켜 말했다(분주 기록) · `module` = 웰 정보가 없어 모듈(배치) 상태를
   * 전 챔버에 입혔다. 화면은 `module` 근거를 «챔버마다 다른 사실»처럼 그리지 않는다 ·
   * `tray` = 서버 트레이 뷰(`GET /monitoring/trays` `chambers[]`)가 그 챔버의 «자리»(로터 칸 · 틸트 · 셰이커 · 어느 트레이)를
   * 가리켜 말했다(2026-10-07 · eric 「챔버별 진행은 서버가 아직 보내지 않음.. 내용 수정한거 아니야?」).
   */
  basis: 'well' | 'module' | 'tray' | 'tracking'
  track?: ChamberTrack
  /**
   * 🔴 **이번 한 바퀴에서 가장 최근에 분주한 웰인가**(웰 근거 · `done` 일 때만). 3D 스포이드가 이 웰에만 선다
   * (`chamberTool`). 「지금 분주 중인 웰」이 아니다 — 서버가 그 값을 안 준다. 서버가 가장 늦게 적은 한 줄이다.
   */
  latest?: true
  /**
   * 웰 근거일 때 — **이번 한 바퀴의 공정 스텝**(`step_ref` 원문). 분주는 시약마다 한 바퀴씩 돈다 —
   * 새 바퀴가 시작되면 앞 바퀴의 「완료」 웰이 「준비 중」으로 돌아가는데, 그 까닭을 글자 옆(툴팁)에 적는 자리다.
   * 바퀴가 비었으면(기록 0건) 없다.
   */
  passStep?: string
  /** 웰 근거일 때 — 그 웰의 이번 분주 한 줄(서버 값 그대로) */
  dispense?: {
    stepRef: string
    at: string
    result?: DispenseResult
  }
}

/**
 * **이번 분주 한 바퀴** — 웰 → 그 웰의 이번 분주 기록.
 *
 * 분주는 같은 공정 스텝(`step_ref`)을 웰마다 한 번씩 돈다(실측: `s01a_gel_dpbs_fill` 이 A1 → A2 → B1 … 8초 간격).
 * 가장 최근 기록부터 거슬러 올라가며 **같은 `step_ref` 이고 아직 안 본 웰**을 모은다. `step_ref` 가 바뀌거나
 * 이미 본 웰이 다시 나오면(같은 스텝의 앞 바퀴) 멈춘다.
 *
 * 🔴 **흡입(`aspirate`)은 넣지 않는다** — 분주한 웰을 세는 자리다. 모르는 값(`operation: null`)은 분주로 읽는다
 *    (열 신설 이전 행 · 계약 원문).
 * ⚠ 기록이 없으면 빈 지도다 — 「아직 한 웰도 안 받았다」이다.
 */
export function currentDispensePass(
  records: readonly DispenseRecord[],
): ReadonlyMap<string, DispenseRecord> {
  const dispensed = records
    .filter((record) => record.operation !== 'aspirate')
    .toSorted((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0))
  const pass = new Map<string, DispenseRecord>()
  const first = dispensed[0]
  if (first === undefined) return pass
  for (const record of dispensed) {
    if (record.step_ref !== first.step_ref || pass.has(record.wellName)) break
    pass.set(record.wellName, record)
  }
  return pass
}

/** 배치 추적 중 이 판정이 읽는 칸 — `GET /batches/{id}/tracking` 응답(`BatchTracking`) · 구독 `batch_tracking` 이 같은 키를 다시 받게 한다 */
export type TransferTrackingLike = Pick<BatchTracking, 'batch_id'> & {
  trays: readonly Pick<TrayTrack, 'where'>[]
}

/**
 * 트레이가 «6축 로봇 쪽»에 있는 자리 — 계약 `TrayTrack.where` 원값 그대로.
 * `robot` = 집음 뒤 놓음 전 로봇 위 · `in_transit` = 이송 시작 뒤 집음 기록 없음(집음 전후 모름).
 * ⚠ `quarantined` 도 `transit` 을 싣지만 넣지 않는다 — 격리는 멈춘 것이지 옮기는 중이 아니다.
 */
const TRANSFER_WHERE: ReadonlySet<TrayTrackWhere> = new Set<TrayTrackWhere>(['robot', 'in_transit'])

/**
 * **이 배치가 지금 6축 이송 중인가** — 그 배치 추적의 트레이 하나라도 `where` 가 `robot` · `in_transit` 이면 참(서버 v0.9.4).
 *
 * 🔴 **근거는 서버 배치 추적 하나다.** 6축 진행 프레임 · 스냅샷 실행에는 `batch_id` 가 없다(2026-10-06 dev2 실측 · 코어가 일부러
 *    뺐다). v0.9.3 까지는 `robot` 자원 점유로 갈랐는데 v0.9.4 에서 자원 점유가 걷히고 **배치 추적**이 그 자리를 이었다 —
 *    서버가 이송 시작 · 집음 · 놓음을 커밋할 때 `batch_tracking` 프레임을 내고, 브리지가 그 배치의 추적을 다시 받는다
 *    (`@ctcells/api` `stream/bridge.ts`). 6축 모듈 상태를 다시 읽어 짐작하지 않는다.
 * 🔴 **못 받았으면(`undefined` — 조회 중 · 실패) 거짓이다** — 이송 중 표시만 안 설 뿐 화면은 그대로다(부가 조회).
 *    「이송 아님」이라고 적는 글자는 어디에도 없다 — 못 본 것을 아니라고 단정하지 않는다.
 * 🔴 **다른 배치의 추적이면 거짓이다** — 캐시 키가 배치별이지만, 넘겨받은 값의 `batch_id` 를 한 번 더 맞댄다.
 * 🔴 **표지를 앞당기지 않는다** — 이송이라고 `centrifuged` · `assembled` 로 올리지 않는다(`chamberProgress` 의 `transfer`).
 */
export function batchTransferring(
  tracking: TransferTrackingLike | undefined,
  batchId: string | undefined,
): boolean {
  if (tracking === undefined || batchId === undefined) return false
  if (tracking.batch_id !== batchId) return false
  return tracking.trays.some((tray) => TRANSFER_WHERE.has(tray.where))
}

export interface ChamberProgressInput {
  /** 그 챔버의 웰 이름(레시피 `sample_groups[].wells`) */
  well: string
  /** 배치 표지가 말하는 공정 자리(`chamberPhase`) — 없으면 아무 말도 안 한다 */
  phase: ChamberPhase | undefined
  /**
   * 이번 분주 한 바퀴(`currentDispensePass`). 🔴 **못 받았으면(`undefined`) 웰 근거를 세우지 않는다** —
   * viewer 403 · 조회 중 · demo 를 「아무 웰도 안 받았다」로 접지 않는다.
   */
  pass: ReadonlyMap<string, DispenseRecord> | undefined
  /**
   * 🔴 **6축 로봇이 이 배치의 트레이를 옮기는 중인가** — 그 배치 추적의 트레이가 `robot` · `in_transit` 일 때만 참이다
   *    (`batchTransferring` · `useSampleFlow` 가 넘긴다). 모듈 상태를 다시 읽어 짐작하지 않는다.
   * 참이면 `transfer` 로 선다(아래 표). 안 넘기면 거짓이다.
   */
  transfer?: boolean | undefined
  /**
   * 🔴 **이 배치의 마지막 실행이 끝났는가** — 이 배치의 마지막 진행 프레임이 종착(`complete` 등)이다(`isTerminalExecutionEvent`).
   *    아래 표의 「실행 끝 · 등록 표지뿐」 칸에만 쓴다. 안 넘기면 거짓이다.
   */
  settled?: boolean | undefined
  /** 서버 트레이 뷰의 그 챔버(`MonitoringTraysView.chambers[]` 에서 웰로 찾은 것) — 없으면 모듈 근거로 떨어진다 */
  chamber?: TrayChamberView | undefined
}

/**
 * 챔버 한 개의 진행. **`undefined` 면 말할 것이 없다**(배치 표지가 하나도 없다).
 *
 * | 배치 자리 | 분주 기록 | 결과 |
 * | --- | --- | --- |
 * | 염색 진행 중 | 받음 · 이 웰 있음 | `done`(결과가 `ok` 가 아니면 `failed`) · `well` |
 * | 염색 진행 중 | 받음 · 이 웰 없음 | `pending` · `well` |
 * | 염색 진행 중 | 못 받음 | `active` · `module`(종전 그대로) |
 * | 등록만 됨 | — | `pending` · `module` |
 * | 그 밖(원심 · 어셈블러 · 끝난 단계) | — | `active`/`done` · `module` — **전 챔버가 같다** |
 * | **6축 이송 중**(`transfer`) | — | `transfer` · `module` — 단계는 «마지막 표지» 그대로 |
 * | **실행 끝 · 등록 표지뿐**(`settled`) | — | `settled` · `module` — 「준비 중」이 아니다 · 「완료」도 아니다 |
 *
 * 🔴 **`settled` 는 원심 실행이 끝난 직후의 틈이다**(2026-10-06 dev2 실측 · batch `9a624a80`): 원심 배치 실행 `complete` 06:29:22.323 →
 *    이송 락 생성(22.6~23.1 · v0.9.3 — v0.9.4 는 배치 추적 `in_transit`) → 6축 첫 변화 23.9. 그 사이 «지금 도는 장비»가 비고(`useRunningDeviceKey` — 종착은 「지금」이 아니다)
 *    시료는 아직 `registered` 라 「원심분리기 · 준비 중」이 1~2초 섰다. 원심이 다 돈 챔버를 「준비 중」이라 말하는 거짓이다.
 *    ⇒ 공정명만 세운다(`chamberNote` → `stage` · 배지 없음). 「완료」로 올리지 않는다 — `centrifuged` 표지는 아직 안 왔다(앞당기지 않는다).
 *    다음 장비도 짐작하지 않는다.
 *
 * 🔴 **이송 중은 「준비 중」이 아니다**(eric 2026-10-06 · 히나 재현 `8c8531c0`). 원심 실행이 끝나고 6축이 트레이를 옮기는
 *    사이(약 40초) 서버의 `centrifuged` 표지가 아직 안 와 시료는 `registered` 에 있다 — 그때 이 함수가 `registered` →
 *    `pending` 을 내 칸마다 「원심분리기 · 준비 중」(점선)이 섰다. 원심이 다 돈 챔버를 「준비 중」이라 말하는 거짓이었다.
 *    ⇒ 이송 중이면 단계는 마지막 표지 그대로 두고(그림 안의 내용물이 그 단계로 남는다) 상태만 `transfer` 다.
 * 🔴 **표지를 앞당겨 짐작하지 않는다** — 이송이 시작됐다고 `centrifuged` · `assembled` 로 올리지 않는다. 레시피로 추론하지 않는다.
 * 🔴 **트레이째 옮기므로 이송은 전 챔버에 같은 사실이다** — 챔버별 진행 모름(BR-98)과 결이 다르다.
 */
export function chamberProgress(input: ChamberProgressInput): ChamberProgress | undefined {
  const { well, phase, pass, transfer = false, settled = false, chamber } = input
  if (phase === undefined) return undefined

  /* 🔴 이송 중 — 마지막 표지의 단계 그대로 · 상태만 이송(위 표). 웰 근거보다 앞이다(염색 중에는 6축이 안 돈다) */
  if (transfer) return { stage: phase.stage, status: 'transfer', basis: 'module' }
  /* 🔴 실행 끝 · 등록 표지뿐 — 「준비 중」으로 되돌아가지 않는다(위 표) */
  if (settled && phase.stage === 'registered')
    return { stage: 'registered', status: 'settled', basis: 'module' }

  if (phase.stage === 'staining' && !phase.done && pass !== undefined) {
    const record = pass.get(well)
    const passStep = pass.values().next().value?.step_ref
    const step = passStep === undefined ? {} : { passStep }
    if (record === undefined)
      return { stage: 'staining', status: 'pending', basis: 'well', ...step }
    /* 한 바퀴 지도는 최근 기록부터 채워진다(`currentDispensePass`) — 첫 키가 가장 최근 웰이다 */
    const latest = pass.keys().next().value === well
    const status = record.result === undefined || record.result === 'ok' ? 'done' : 'failed'
    return {
      stage: 'staining',
      /* 🔴 결과를 모르면(키 없음) 실패로 짐작하지 않는다 — 기록이 있다는 사실만 말한다 */
      status,
      basis: 'well',
      ...step,
      ...(latest && status === 'done' ? { latest: true as const } : {}),
      dispense: {
        stepRef: record.step_ref,
        at: record.time,
        ...(record.result === undefined ? {} : { result: record.result }),
      },
    }
  }

  /* 🔵 서버 트레이 뷰가 그 챔버의 자리를 말하면 칸마다 칠한다(`tray` 근거) — 염색 분주 기록(웰 근거)보다 뒤 · 모듈 근거보다 앞 */
  const placed = chamber === undefined ? undefined : trayChamberProgress(chamber)
  if (placed !== undefined) return placed

  if (phase.stage === 'registered')
    return { stage: 'registered', status: 'pending', basis: 'module' }
  return { stage: phase.stage, status: phase.done ? 'done' : 'active', basis: 'module' }
}

/**
 * **서버 트레이 뷰의 자리 → 그 챔버의 진행** — 서버 `services/tray_view.py` 가 챔버를 옮기는 규칙 그대로 읽는다
 * (C 트레이 → 로터 칸 → A 트레이 → 틸트 스테이지 → L 트레이 → 셰이커 → L 트레이 · 뒤로 가지 않는다).
 *
 * | 자리(`place` · `tray`) | 뜻 | 진행 |
 * |---|---|---|
 * | `rotor_slot` | 원심 로터 안 | 원심 `active` |
 * | `tray` C · `active` | 지금 로터로 싣는 중 | 원심 `active` |
 * | `tray` C | 아직 원심 전 | **말하지 않는다**(`undefined`) — 시작 전과 같은 자리라 모듈 근거가 그대로 「준비 중」을 낸다 |
 * | `tray` A · `active` | 지금 틸트로 집는 중 | 어셈블러 `active` |
 * | `tray` A | 원심을 마치고 어셈블러 대기 | 원심 `done` |
 * | `tilt_stage` | 어셈블러 틸트 스테이지 위 | 어셈블러 `active` |
 * | `shaker` | 염색기 셰이커 위 | 염색 `active` |
 * | `tray` L · 시료 단계 `completed` | 염색까지 끝남 | 완료 `done` |
 * | `tray` L | 어셈블러를 마치고 염색 대기 | 어셈블러 `done` |
 *
 * 🔴 시료가 없는 칸(`sample_id` 없음)·모르는 자리는 `undefined` — 지어내지 않고 모듈 근거로 떨어진다.
 * 🔴 `active` 는 서버가 「지금 그 공정이 이 챔버를 다룬다」고 한 표지다(`tray_view.py` `chambers[w].active`) — 여기서 셈하지 않는다.
 */
export function trayChamberProgress(chamber: TrayChamberView): ChamberProgress | undefined {
  if (chamber.sample_id === null) return undefined
  const basis = 'tray' as const
  switch (chamber.place) {
    case 'rotor_slot':
      return { stage: 'centrifuged', status: 'active', basis }
    case 'tilt_stage':
      return { stage: 'assembled', status: 'active', basis }
    case 'shaker':
      return { stage: 'staining', status: 'active', basis }
    case 'tray':
      break
    default:
      return undefined
  }
  switch (chamber.tray) {
    case 'C':
      return chamber.active ? { stage: 'centrifuged', status: 'active', basis } : undefined
    case 'A':
      return chamber.active
        ? { stage: 'assembled', status: 'active', basis }
        : { stage: 'centrifuged', status: 'done', basis }
    case 'L':
      if (chamber.stage === 'completed') return { stage: 'completed', status: 'done', basis }
      return chamber.active
        ? { stage: 'staining', status: 'active', basis }
        : { stage: 'assembled', status: 'done', basis }
    default:
      return undefined
  }
}
